import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  describeOrderItem,
  formatQuantityDifference,
  getOrderItemControlId,
  getReceivedDifference,
  ITEM_REQUEST_VARIANT_COPY,
  type ItemRequestVariant,
  type SelectableOrderItem,
} from "./supportRequest";

const quantityInputId = (itemId: string) => `order-item-quantity-${itemId}`;
const itemErrorId = (itemId: string) => `order-item-error-${itemId}`;

interface OrderItemCardProps {
  item: SelectableOrderItem;
  variant: ItemRequestVariant;
  selected: boolean;
  onToggle: (itemId: string, selected: boolean) => void;
  /** Valor digitado: quantidade da troca (até a pedida) ou quantidade recebida na conferência. */
  quantity: string;
  onQuantityChange: (itemId: string, quantity: string) => void;
  /** Mesma informação da coluna "Tipo" da tabela de produtos (Aluno/Professor…). */
  usage?: string | null;
  error?: string;
  disabled?: boolean;
}

/**
 * Card de um item no seletor, com a marcação quadrada do protótipo. Marcado, ganha o campo de quantidade no próprio
 * card, sempre obrigatório: na troca, quantos trocar (até a quantidade pedida); na conferência, quanto chegou e a
 * diferença.
 */
export const OrderItemCard = ({
  item,
  variant,
  selected,
  onToggle,
  quantity,
  onQuantityChange,
  usage,
  error,
  disabled,
}: OrderItemCardProps) => {
  const controlId = getOrderItemControlId(item.id);
  const isConference = variant === "conference";
  const difference = isConference && selected ? getReceivedDifference(item, quantity) : null;

  return (
    <div
      className={cn(
        "flex flex-col rounded-[10px] border transition-colors",
        selected ? "border-2 border-primary bg-primary/5" : "hover:bg-muted/40",
        disabled && "opacity-60"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1">
        <label
          htmlFor={controlId}
          className={cn(
            "flex min-w-0 flex-1 basis-[240px] cursor-pointer items-center gap-3.5 py-3.5 pl-4",
            selected && "py-[13px] pl-[15px]",
            disabled && "cursor-not-allowed"
          )}
        >
          <Checkbox
            id={controlId}
            checked={selected}
            onCheckedChange={(checked) => onToggle(item.id, checked === true)}
            disabled={disabled}
            className="h-5 w-5 rounded-[5px] border-[1.5px] border-input bg-background data-[state=checked]:border-primary [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:stroke-[3]"
          />
          <span className="flex min-w-0 flex-col gap-1">
            <span className="text-[15px] font-semibold leading-snug">{item.productDescription}</span>
            <span className="text-[13px] text-muted-foreground">{describeOrderItem(item, usage)}</span>
          </span>
        </label>

        {selected ? (
          <div className="flex shrink-0 items-center gap-2.5 pb-3 pl-[51px] pr-[15px] sm:pb-0 sm:pl-0">
            <label htmlFor={quantityInputId(item.id)} className="text-[13px] text-muted-foreground">
              {ITEM_REQUEST_VARIANT_COPY[variant].quantityLabel}
              <span className="sr-only">
                {" "}
                de {item.productDescription}
                {/* A quantidade pedida não aparece ao lado do campo: só o leitor de tela a recebe. */}
                {" "}(pedido: {item.quantityOrdered})
              </span>
            </label>
            <span
              className={cn(
                "flex h-9 items-center rounded-lg border bg-background px-1 focus-within:ring-2 focus-within:ring-ring",
                error && "border-destructive"
              )}
            >
              <input
                id={quantityInputId(item.id)}
                type="number"
                inputMode="numeric"
                min={isConference ? 0 : 1}
                max={isConference ? undefined : item.quantityOrdered}
                step={1}
                value={quantity}
                onChange={(e) => onQuantityChange(item.id, e.target.value)}
                disabled={disabled}
                aria-required
                aria-invalid={!!error}
                aria-describedby={error ? itemErrorId(item.id) : undefined}
                className="w-12 bg-transparent text-center text-[15px] font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </span>
            {difference !== null && difference !== 0 && (
              <span
                className="min-w-[2.5rem] rounded-md bg-amber-100 px-1.5 py-0.5 text-center text-[13px] font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                aria-live="polite"
              >
                <span className="sr-only">Diferença: </span>
                {formatQuantityDifference(difference)}
              </span>
            )}
          </div>
        ) : (
          <span className="shrink-0 pr-4 text-[13px] text-muted-foreground">Qtd. {item.quantityOrdered}</span>
        )}
      </div>

      {error && (
        <p
          id={itemErrorId(item.id)}
          role="alert"
          className={cn("pb-3 pl-[50px] pr-4 text-[13px] font-medium text-destructive", selected && "pl-[51px]")}
        >
          {error}
        </p>
      )}
    </div>
  );
};
